import { cn } from "@win-reality/react-ui";
import { useSelector } from "react-redux";

import type { RootState } from "../../../store/store";

const badgeBgClass = {
  hitter: "bg-badge-batter",
  pitcher: "bg-badge-pitcher",
  catcher: "bg-badge-catcher",
} as const;

interface ActivePlayerTileProps {
  player?: Partial<IPlayer> | null | undefined;
  handleClick?: () => void;
  playerType?: "hitter" | "pitcher" | "catcher";
  pitchCount?: number;
  clickable?: boolean;
  condensed?: boolean;
  hidePitchCount?: boolean;
}

/**
 * ActivePlayerTile - Displays active player information
 *
 * Shows the current hitter, pitcher, or catcher with:
 * - Player position badge (AB, P, C)
 * - Jersey number
 * - Player name
 * - Pitch count badge (pitcher only)
 *
 * Supports:
 * - Clickable tiles that open substitution modals
 * - Condensed mode for smaller screens
 * - Viewing previous events (hides pitch count)
 */
export const ActivePlayerTile = ({
  player,
  playerType,
  pitchCount,
  handleClick,
  clickable = false,
  condensed = false,
  hidePitchCount = false,
}: ActivePlayerTileProps) => {
  const { isViewingPreviousEvent } = useSelector(
    (state: RootState) => state.socket,
  );

  // Position badge label
  const positionLabel =
    playerType === "hitter" ? "AB" : playerType === "pitcher" ? "P" : "C";

  // Condensed mode (mobile)
  if (condensed) {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        className={cn(
          // Size
          "h-11 w-36",
          // Layout
          "relative flex flex-row items-center justify-start",
          // Spacing
          "px-2.5 py-1.5",
          // Border
          "rounded",
          // Background
          "bg-white",
          // Effects
          "shadow-md",
          // States
          clickable && "cursor-pointer hover:bg-[hsla(218,100%,52%,0.05)]",
        )}
        onClick={() => handleClick && handleClick()}
      >
        {/* Pitch Count Badge (pitcher only, condensed) */}
        {playerType === "pitcher" &&
          !isViewingPreviousEvent &&
          !hidePitchCount && (
            <div
              className={cn(
                // Size
                "h-10 w-10",
                // Layout
                "absolute -top-5 -right-5 flex items-center justify-center",
                // Border
                "rounded-full border-4 border-white",
                // Background
                "bg-orange-200",
              )}
            >
              <span className="text-sm font-semibold text-charcoal-900">
                {pitchCount}
              </span>
            </div>
          )}

        {/* Position Badge */}
        {playerType && (
          <div
            className={cn(
              "flex h-7 min-w-[30px] items-center justify-center rounded",
              badgeBgClass[playerType],
            )}
          >
            <span className="text-xs font-semibold text-white">
              {positionLabel}
            </span>
          </div>
        )}

        {/* Player Name (Last name only) */}
        <div className="flex items-center justify-center px-1.5">
          {player?.lastName === undefined ? (
            <span className="text-xs text-text-light">---</span>
          ) : (
            <span className="text-sm text-text-light">
              {player.lastName.slice(0, 12)}
            </span>
          )}
        </div>
      </div>
    );
  }

  // Full mode (desktop)
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={cn(
        // Size
        "h-11 max-w-[300px] min-w-[200px]",
        // Layout
        "relative flex flex-row items-center justify-start",
        // Spacing
        "px-2.5 py-1.5",
        // Border
        "rounded",
        // Background
        "bg-white",
        // Effects
        "shadow-md",
        // States
        clickable && "cursor-pointer hover:bg-[hsla(218,100%,52%,0.05)]",
      )}
      onClick={() => handleClick && handleClick()}
    >
      {/* Pitch Count Badge (pitcher only, full mode) */}
      {playerType === "pitcher" &&
        !isViewingPreviousEvent &&
        !hidePitchCount && (
          <div
            className={cn(
              // Size
              "h-10 w-10",
              // Layout
              "absolute -top-5 -right-5 flex items-center justify-center",
              // Border
              "rounded-full border-4 border-white",
              // Background
              "bg-badge-pitcher",
            )}
          >
            <span className="text-sm font-semibold text-charcoal-900">
              {pitchCount}
            </span>
          </div>
        )}

      {/* Position Badge */}
      {playerType && (
        <div
          className={cn(
            "flex h-7 min-w-[30px] items-center justify-center rounded",
            badgeBgClass[playerType],
          )}
        >
          <span className="text-xs font-semibold text-white">
            {positionLabel}
          </span>
        </div>
      )}

      {/* Jersey Number */}
      {player?.jerseyNumber && (
        <div className="flex items-center justify-center px-2 py-1">
          <span className="text-[10px] italic text-text-light">
            #{player.jerseyNumber}
          </span>
        </div>
      )}

      {/* Player Name (Last, First Initial) */}
      <div className="flex flex-1 items-center justify-start px-4">
        {player?.lastName === undefined ? (
          <span className="text-sm text-text-light text-center">---</span>
        ) : (
          <span className="text-sm text-text-light">
            {`${player.lastName}, ${player.firstName?.[0]}`}
          </span>
        )}
      </div>
    </div>
  );
};
